import { useState } from 'react';
import { MessageSquareText, Languages } from 'lucide-react';

/**
 * Shared feedback display card with KR/EN toggle.
 * Used by ModuleDropdown (class feedback) and GradeCard (exam feedback).
 *
 * @param {string} label - e.g., "Class Feedback", "Midterm Feedback"
 * @param {string} textKr - Korean feedback text
 * @param {string} textEn - English feedback text
 * @param {string} defaultLang - 'en' or 'kr' (default: 'en')
 */
export default function FeedbackCard({ label, textKr, textEn, defaultLang = 'en' }) {
  const [language, setLanguage] = useState(defaultLang);

  if (!textKr && !textEn) return null;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg p-3 sm:p-4 border border-blue-100 dark:border-blue-900">
      <div className="flex items-start gap-2 sm:gap-3">
        <div className="flex-shrink-0 w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center">
          <MessageSquareText className="w-3 h-3 sm:w-4 sm:h-4 text-blue-400" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-1">
            <span className="text-[10px] sm:text-xs font-semibold text-blue-400 uppercase tracking-wider">
              {label}
            </span>
            {textEn && textKr && (
              <div className="flex items-center gap-1">
                <Languages className="w-3 h-3 text-gray-400 dark:text-gray-500" />
                <div className="flex rounded overflow-hidden border border-gray-200 dark:border-gray-600">
                  <button
                    onClick={(e) => { e.stopPropagation(); setLanguage('kr'); }}
                    className={`px-1.5 py-0.5 text-[10px] font-medium transition-colors ${
                      language === 'kr'
                        ? 'bg-blue-400 text-white'
                        : 'bg-white dark:bg-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-600'
                    }`}
                  >
                    KR
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); setLanguage('en'); }}
                    className={`px-1.5 py-0.5 text-[10px] font-medium transition-colors ${
                      language === 'en'
                        ? 'bg-blue-400 text-white'
                        : 'bg-white dark:bg-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-600'
                    }`}
                  >
                    EN
                  </button>
                </div>
              </div>
            )}
          </div>
          <p className="text-xs sm:text-sm text-gray-700 dark:text-gray-300 mt-1 leading-relaxed whitespace-pre-wrap">
            {language === 'en' && textEn ? textEn : textKr}
          </p>
        </div>
      </div>
    </div>
  );
}
